
import React, { createContext, useContext, useState, useMemo } from 'react';
import { ROLE_APAVE_CLIENT, ROLE_ADMIN, ROLE_USER, ROLE_SUPPORT } from 'config';
import _ from 'lodash';
import { appProfileSelector } from 'redux/selectors';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { setLoading } from 'redux/actions/app';
import * as API from 'services/api';
import RemoveUserModal from 'components/Modals/RemoveUserModal';
import { showMessage } from 'utils';
import { getClientApiErrorMessage } from 'utils/clientApiErrors';
import ImportUsersModal from 'components/Modals/ImportUsersModal';

export default function useHomeContext() {
    const context = useContext(HomeContext);
    if (!context) {
        throw new Error('useHomeContext must be used within a HomeProvider');
    }
    return context;
}

const HomeContext = createContext();

export const HomeProvider = ({ children }) => {
    const dispatch = useDispatch();
    const {
        role,
        adminPermissions
    } = useSelector(appProfileSelector);
    const [importUsersModalVisible, setImportUsersModalVisible] = useState(false);
    const hasPermission = (path) => {
        if (role == ROLE_APAVE_CLIENT) {
            try {
                return adminPermissions && path ? _.get(adminPermissions, path, false) : false
            } catch (error) {
                console.log("no permission:", error)
                return false
            }
        } else if (role == ROLE_ADMIN || role === ROLE_SUPPORT) {
            return true;
        }
    };
    //import functions
    const toggleUsersImportModal = () => {
        setImportUsersModalVisible();
    };
    const onUploadUsers = (apiName, { file, type, clientId, role }, callback) => {
        dispatch(setLoading(true));
        const formData = new FormData();
        formData.append('type', type);
        formData.append('file', file);
        if (role)
            formData.append('role', role);
        if (clientId)
            formData.append('clientId', clientId);
        API[apiName](formData)
            .then(res => {
                dispatch(setLoading(false));
                if (res.code === 200) {
                    toggleUsersImportModal();
                    if (callback) callback();
                    else window.location.reload();
                } else {
                    showMessage({
                        message: res.message,
                        type: 'danger',
                    });
                }
            })
            .catch(err => {
                dispatch(setLoading(false));
            });
    };

    //delete function
    const [removeModalInfo, setRemoveModalInfo] = useState({
        open: false,
        id: null,
    });

    const handleCloseRemoveModal = confirmed => {
        if (confirmed) {
            deleteUser(removeModalInfo);
        }
        setRemoveModalInfo({
            open: false,
            id: null,
            description: ""
        });
    };
    const deleteUser = ({ id, apiName, callback }) => {
        dispatch(setLoading(true));
        API[apiName || "deleteUser"](apiName == "updateCrossSubmission" ? {
            id,
            toDelete: true
        } : id)
            .then(res => {
                dispatch(setLoading(false));
                if (res.code === 200) {
                    showMessage({
                        message: 'removed successfully',
                        type: 'success'
                    });
                    if (callback) callback();
                    else window.location.reload();
                }
            })
            .catch(err => {
                dispatch(setLoading(false));
            });
    };

    const onDeleteById = (id, apiName, callback) => {
        setRemoveModalInfo({
            open: true,
            id,
            description: "Merci de confirmer à nouveau.",
            apiName,
            callback
        });
    };

    const createClient = (userInfo, formatMessage) => {
        dispatch(setLoading(true));
        API.createClient(userInfo)
            .then(res => {
                dispatch(setLoading(false));
                if (res && res.code === 201) {
                    showMessage({
                        message: 'Company is created successfully',
                        type: 'success',
                    });
                } else {
                    const message = formatMessage
                        ? (getClientApiErrorMessage(res, formatMessage) || formatMessage({ id: 'home.client.createFailed' }))
                        : (res.message || 'Failed to create company');
                    showMessage({
                        message,
                        type: 'danger'
                    });
                }
            })
            .catch(err => {
                console.warn(err);
                alert(err.message);
                dispatch(setLoading(false));
            });
    };

    const values = useMemo(() => ({
        hasPermission,

        onUploadUsers,
        importUsersModalVisible,
        setImportUsersModalVisible,

        onDeleteById,
        createClient
    }), [
        adminPermissions,
        importUsersModalVisible,
        removeModalInfo
    ]);
    return (
        <HomeContext.Provider
            value={values}
        >
            {children}
            <RemoveUserModal
                open={removeModalInfo.open}
                description={removeModalInfo.description}
                onCloseModal={handleCloseRemoveModal}
            />
            <ImportUsersModal
                addFree={importUsersModalVisible?.addFree}
                visible={!!importUsersModalVisible}
                toggleModal={toggleUsersImportModal}
                onUpload={(file, type, clientId) => onUploadUsers(importUsersModalVisible?.apiName, {
                    file,
                    type,
                    ...importUsersModalVisible?.needRole ? {
                        clientId,
                        role: ROLE_USER
                    } : {}
                })}
                title={importUsersModalVisible?.title}
                modelUrl={importUsersModalVisible?.modelUrl}
                hideClient={importUsersModalVisible?.hideClient}
            />
        </HomeContext.Provider>
    );
};

HomeProvider.propTypes = {
    children: PropTypes.any,
};